<div class="h-full flex w-full p-2 gap-2">
    <!-- Left sidebar: bundle selector + cluster list -->
    <div class="border border-gray-200 rounded-base w-[20%] flex flex-col min-w-0">
        <div class="flex flex-col gap-1 p-2 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">{{ 'Clause bundle' | textCase }}</h3>
            <app-select [(value)]="selectedBundleId" [options]="clauseBundleOptions" [config]="bundleSelectConfig"
                (onChange)="selectBundle($event.value)">
            </app-select>
        </div>
        <div class="p-2 flex-1 min-h-0 overflow-auto flex flex-col">
            <h3 class="text-sm font-semibold text-gray-900 mb-2">Duplicate clusters</h3>
            @if (duplicateClusterLoading) {
            <div class="flex justify-center items-center py-8">
                <p-progressSpinner [style]="{ width: '24px', height: '24px' }"></p-progressSpinner>
            </div>
            } @else {
            @for (cluster of duplicateClusterOptions; track cluster.clusterId) {
            <div class="flex items-center justify-between p-2 rounded-base cursor-pointer transition-colors"
                [ngClass]="selectedDuplicateCluster()?.clusterId === cluster.clusterId ? 'bg-arena-orange-100' : 'text-gray-700'"
                (click)="selectDuplicateCluster(cluster)">
                <div class="flex items-center justify-between w-full gap-1.5 min-w-0">
                    <span class="text-sm truncate" pTooltip="{{cluster.title}}" tooltipPosition="bottom">{{
                        cluster.title }}</span>
                    <span class="text-sm flex-shrink-0">({{ cluster.count }})</span>
                </div>
            </div>
            }
            @if (duplicateClusterOptions.length === 0 && !duplicateClusterLoading) {
            <div class="flex flex-1 justify-center items-center">
                <p class="text-sm text-gray-500 text-center">No duplicate clusters found</p>
            </div>
            }
            }
        </div>
    </div>

    <!-- Right: table of clauses in selected cluster -->
    <div class="w-[80%] gap-4 flex flex-col overflow-auto min-w-0 flex-1 min-h-0">
        @if (selectedClauseIds.length > 0) {
        <div class="flex justify-between border rounded-base p-2 border-gray-200 items-center flex-wrap gap-2">
            <div class="flex gap-2 items-center text-sm flex-wrap">
                <span *ngIf="!isSelectAll">{{ selectedClauseIds.length }} items selected</span>
                <span *ngIf="isSelectAll">All {{ duplicateClausesData.length }} items selected</span>
                <div class="border-l border-gray-300 h-auto mx-2"></div>
                @if (selectedClauseIds.length > 1) {
                <app-button label="Merge clauses" variant="filled" severity="primary" [icon]="'merge'"
                    [iconSource]="IconSource.LUCIDE" (clicked)="openMergeDialog()" />
                }
                <app-button label="Mark as not duplicate" variant="filled" severity="primary" [icon]="'copy-minus'"
                    [iconSource]="IconSource.LUCIDE" (clicked)="markNotDuplicateClause($event)" />
            </div>
            <div class="flex items-center gap-2">
                <app-button label="Reject" variant="outlined" severity="danger" [icon]="'pi pi-times'"
                    (clicked)="deleteClauses()" />
                @if (selectedClauseIds.length === 1) {
                <app-button label="Approve" variant="outlined" severity="success" [icon]="'pi pi-check'"
                    (clicked)="acceptClause($event)" />
                }
            </div>
        </div>
        } @else if (selectedDuplicateCluster()) {
        <h2 class="text-sm font-semibold text-gray-900">{{ selectedDuplicateCluster()?.title }}</h2>
        }

        <div class="flex-1 min-h-0 flex flex-col overflow-hidden"
            [style.height]="selectedClauseIds.length > 0 ? 'calc(100vh - 16rem)' : 'calc(100vh - 12rem)'">
            <app-data-table #dataTable [data]="duplicateClausesData" [columns]="duplicateClausesTableColumns"
                [config]="duplicateClausesTableConfig" [dataKey]="'_id'" [customTemplates]="templatesMap"
                [rowClass]="getDuplicateClausesRowClass.bind(this)"
                (selectionChange)="onDuplicateClausesSelectionChange($event)" tableClass="text-sm h-full">
            </app-data-table>
        </div>
    </div>
</div>

<ng-template #clauseNameTemplateRef let-clause="$implicit">
    <div class="text-sm truncate" [pTooltip]="getClauseName(clause)" tooltipPosition="top">
        {{ getClauseName(clause) }}
    </div>
</ng-template>

<ng-template #clauseTextTemplateRef let-clause="$implicit">
    <div class="relative w-full h-full group min-h-14">
        <i class="pi pi-arrow-up-right-and-arrow-down-left-from-center absolute top-1 right-1 text-xs cursor-pointer p-1 border rounded-base bg-white shadow-md z-10 opacity-0 group-hover:opacity-100 transition-opacity"
            (click)="openClauseTextModal(getClauseText(clause), 'Clause text')"></i>
        <div class="prose prose-compact text-justify text-sm prose-ul:pl-1 prose-ol:pl-2 2xl:prose-ul:pl-4 2xl:prose-ol:pl-3 sm:prose-ul:pl-3 h-full overflow-y-auto max-h-24"
            [innerHTML]="getClauseText(clause) | formatText : false" dynamicComponentRenderer>
        </div>
    </div>
</ng-template>

<ng-template #sourceTemplateRef let-clause="$implicit">
    <span class="text-sm truncate block" [pTooltip]="getSourceLabel(clause.source)" tooltipPosition="top">
        {{ getSourceLabel(clause.source) }}
    </span>
</ng-template>

<ng-template #createdByTemplateRef let-clause="$implicit">
    <app-user-name-display [user]="clause.createdBy" />
</ng-template>

<ng-template #createdAtTemplateRef let-clause="$implicit">
    <span class="text-sm truncate block" [pTooltip]="formatDate(clause.createdAt)" tooltipPosition="top">
        {{ formatDate(clause.createdAt) }}
    </span>
</ng-template>

<!-- Expand clause text dialog -->
<app-dialog [(visible)]="expandClauseTextCell" class="w-[80%]">
    <ng-template #headerTemplate>
        <h3 class="font-bold text-base sm:text-lg">{{ expandedClauseTextTitle }}</h3>
    </ng-template>
    <ng-template #bodyTemplate>
        <div class="px-4 pb-4">
            <div class="prose prose-compact text-justify text-md prose-ul:pl-1 prose-ol:pl-2 overflow-x-auto leading-6"
                [innerHTML]="expandedClauseTextContent | formatText : false" dynamicComponentRenderer>
            </div>
        </div>
    </ng-template>
</app-dialog>

<!-- Merge clause dialog -->
<app-dialog [(visible)]="showMergeDialog" (closed)="closeMergeDialog()" class="w-[50vw] max-w-[36rem] overflow-visible">
    <ng-template #headerTemplate>
        <h3 class="font-bold text-base sm:text-lg">{{ 'Merge clause' | textCase }}</h3>
    </ng-template>
    <ng-template #bodyTemplate>
        <div class="flex flex-col gap-4 p-2">
            <div class="flex flex-col gap-2">
                <h4 class="text-sm font-semibold text-gray-900">Clause</h4>
                @if (mergeSuggestionLoading) {
                <div class="flex items-center gap-2 text-sm text-gray-500 italic">
                    <p-progressSpinner [style]="{ width: '16px', height: '16px' }"></p-progressSpinner>
                    Loading suggestion...
                </div>
                } @else {
                <div class="text-sm text-gray-600 italic">{{ mergeSuggestion.name || 'Clause name' }}</div>
                <div class="text-sm text-gray-600 italic line-clamp-3">{{ mergeSuggestion.text || 'Clause text' }}</div>
                }
            </div>
            <div class="flex flex-col gap-2">
                <h4 class="text-sm font-semibold text-gray-900">Duplicate clauses</h4>
                <div
                    class="border border-gray-200 rounded-base overflow-y-auto flex flex-col gap-2 p-2 min-h-[8rem] max-h-[12rem]">
                    @for (clause of mergeClauses; track clause._id) {
                    <div class="flex items-start gap-2 p-2 rounded-base border border-gray-100 bg-gray-50">
                        <div class="flex-shrink-0 pt-0.5" (click)="$event.stopPropagation()">
                            <app-checkbox [value]="mergeClausesSelected.has(clause._id)"
                                (onChange)="selectMergeClause(clause._id)" [config]="{ binary: true }" />
                        </div>
                        <div class="flex flex-col gap-1 min-w-0 flex-1">
                            <span class="text-sm font-medium text-gray-900">{{ getClauseName(clause) }}</span>
                            <span class="text-sm text-gray-600 line-clamp-2">{{ clause.text || '-' }}</span>
                        </div>
                    </div>
                    }
                </div>
            </div>
            <div class="flex flex-col gap-2">
                <div class="flex items-center gap-2">
                    <h4 class="text-sm font-semibold text-gray-900">Clause name</h4>
                    @if (!mergeSuggestionLoading && selectedMergeClausesChanged) {
                    <i class="pi pi-refresh cursor-pointer text-arena-orange text-sm"
                        (click)="getMergeSmartSuggestion()" title="Refresh suggestion"></i>
                    }
                </div>
                <input [(ngModel)]="mergeSuggestion.name" class="w-full rounded-lg border border-gray-200 p-2 text-sm"
                    placeholder="Enter clause name" />
            </div>
            <div class="flex flex-col gap-2">
                <h4 class="text-sm font-semibold text-gray-900">Clause text</h4>
                <textarea [(ngModel)]="mergeSuggestion.text"
                    class="w-full rounded-lg border border-gray-200 p-2 text-sm" rows="4"
                    placeholder="Enter clause text"></textarea>
            </div>
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end items-center gap-2 w-full">
            <app-button [label]="'Approve & save' | textCase" variant="outlined" icon="pi pi-check"
                (clicked)="saveMergeClausePrompt()" [loading]="savingMergeClause"
                [disabled]="mergeSuggestionLoading || savingMergeClause" />
            <app-button label="Save" variant="filled" icon="pi pi-save" (clicked)="saveMergeClause()"
                [loading]="savingMergeClause" [disabled]="mergeSuggestionLoading || savingMergeClause"
                severity="primary" />
        </div>
    </ng-template>
</app-dialog>

@if (showDeleteConfirmDialog) {
<app-delete-confirmation [(visible)]="showDeleteConfirmDialog"
    [headerText]="selectedClauseIds.length > 1 ? 'Delete clauses' : 'Delete clause'"
    [bodyText]="'You are about to delete ' + selectedClauseIds.length + ' duplicate clause' + (selectedClauseIds.length > 1 ? 's' : '') + '. This cannot be undone. Proceed?'"
    confirmButtonText="Yes, remove" (onConfirm)="confirmDeleteClauses()">
</app-delete-confirmation>
}

<p-confirmDialog #cd>
    <ng-template pTemplate="headless" let-message>
        <div class="flex flex-row gap-2 p-5 surface-overlay border-round w-[30rem]">
            @if (message.icon) {
            <div class="mt-1">
                <app-icon [type]="message.icon" [source]="message.iconSource ?? 'primeng'" size="default"
                    [variant]="message.acceptButtonProps?.severity ?? 'default'" />
            </div>
            }
            <div class="flex flex-col align-items-center">
                <span class="font-bold text-md block mb-2">{{ message.header }}</span>
                <p class="mb-0">{{ message.message }}</p>
                <div class="flex justify-end items-end float-left gap-2 mt-4">
                    <app-button [label]="message.rejectLabel ?? 'No'" variant="outlined" severity="contrast"
                        (clicked)="handleConfirmReject(message)" />
                    <app-button [label]="message.acceptLabel ?? 'Yes'"
                        [variant]="message.acceptButtonProps?.variant ?? 'filled'"
                        [severity]="message.acceptButtonProps?.severity ?? 'primary'"
                        (clicked)="handleConfirmAccept(message)" />
                </div>
            </div>
        </div>
    </ng-template>
</p-confirmDialog>